import { useEffect, useMemo } from 'react'
import {
  type FiatRegionVerdict,
  resolveFiatRegionSupport,
  useEffectEvent,
} from '@funkit/connect-core'

import { logger } from '../logging/logger'
import { useDynamicConfig } from '../statsig/useDynamicConfig'
import { useStatsigReady } from '../statsig/useStatsigReady'
import { useUserIpInfo } from './useUserIpInfo'

export interface UseFiatRegionSupportResult {
  verdict: FiatRegionVerdict
  /**
   * The verdict is provisional: either the buyer's location or the policy
   * itself is still resolving. A provisional verdict always reads as supported,
   * so a caller that acts on it admits a buyer the settled policy may deny.
   */
  isLoading: boolean
}

/**
 * Whether fiat deposits are served where the buyer is, from their IP alone.
 *
 * Both inputs are already cached by the screens that follow — the geo lookup
 * is session-stable and the config is the one Statsig resolves against that
 * same country — so asking here costs no request and no wait.
 *
 * While the location is resolving the verdict reads as supported, which is
 * what keeps a buyer off a refusal we have not decided yet. A location that
 * never resolves is the existing compliance gate's to answer, not this one's.
 */
export function useFiatRegionSupport(): UseFiatRegionSupportResult {
  const { userIpInfo, isLoadingGeoCheck } = useUserIpInfo()
  const isStatsigReady = useStatsigReady()
  const policy = useDynamicConfig('fiatv2regionpolicy')
  const isLoading = isLoadingGeoCheck || !isStatsigReady

  const verdict = useMemo(
    () =>
      resolveFiatRegionSupport(
        policy,
        userIpInfo === undefined
          ? undefined
          : { alpha2: userIpInfo.alpha2, state: userIpInfo.state },
      ),
    [policy, userIpInfo],
  )

  const announce = useEffectEvent(() => {
    // Nothing to report until the verdict is the settled policy's.
    if (isLoading || userIpInfo === undefined) {
      return
    }
    logger.info('connect-rn:fiat:region_gate', {
      alpha2: userIpInfo.alpha2,
      state: userIpInfo.state,
      supported: verdict.supported,
      deferred: verdict.deferred ?? false,
      matched: verdict.matched,
      source: verdict.source,
      denyCount: policy?.deny?.length ?? 0,
      allowCount: policy?.allow?.length ?? 0,
    })
    if (verdict.skipped !== undefined) {
      // The buyer's own subdivision never reached us, so entries scoped to one
      // could not be judged. Recovered, but it is how a served region gets
      // refused — or a refused one served.
      logger.warn('connect-rn:fiat:region_undecidable', {
        alpha2: userIpInfo.alpha2,
        skipped: verdict.skipped,
        deferred: verdict.deferred ?? false,
      })
    }
  })

  useEffect(() => {
    announce()
  }, [verdict, isLoading])

  return { verdict, isLoading }
}
